import type { FunctionComponent } from 'react';
import cx from 'clsx';
import { WCCFooterItems } from './constants';

/**
 * Please note that the footer uses grid areas to layout the content.
 * To use this component, user should ensure that the gridTemplateColumns and gridTemplateAreas are defined in the tailwind.config.js file
 * Additionally, to use it with tailwind, @savvywombat/tailwindcss-grid-areas plugin should be added in the talwind.config.js
 */

const Feedback = () => (
  <div
    className={cx(
      'grid-in-feedback-link',
      'bg-accent-highlight w-full -translate-y-8 rounded-[0.45rem] px-[20px] py-[5px] lg:-translate-y-6',
    )}
  >
    <a
      href={WCCFooterItems.feedback.url}
      className="text-text-body hover:text-text-dark rounded-[4.5px] leading-[1.2] underline"
    >
      {WCCFooterItems.feedback.text}
    </a>
  </div>
);

const FooterMenu = () => (
  <nav
    className={cx('grid-in-top-links', 'flex flex-col text-left')}
    aria-label="Council information"
  >
    {WCCFooterItems.menu.map((item) => (
      <div key={item.url} className="mb-[8.5px]">
        <a
          href={item.url}
          className={`hover:bg-action-button-hover focus:text-brand-primary focus:bg-action-focus focus:outline-action-focus text-body rounded-[4.5px] text-white lg:font-[600]`}
          rel="noopener noreferrer"
          target="_blank"
        >
          {item.text} <span className="sr-only">(link opens new window)</span>
        </a>
      </div>
    ))}
  </nav>
);

const FooterLogo = () => (
  <a
    href={WCCFooterItems.logo.url}
    className={cx(
      'grid-in-logo',
      `focus:text-brand-primary focus:bg-action-focus focus:outline-action-focus text-body mb-[2rem] max-w-[8rem] rounded-[4.5px] text-white md:max-w-none lg:mb-0`,
    )}
    rel="noopener noreferrer"
    target="_blank"
  >
    <img
      className="wcc-image"
      src={WCCFooterItems.logo.src}
      alt={WCCFooterItems.logo.alt}
    />
  </a>
);

const SocialMedia = () => (
  <nav
    className={cx('grid-in-social-media', 'mt-4 flex gap-x-3')}
    aria-label="Social media"
  >
    {WCCFooterItems.socialMedia.map((item) => (
      <a
        key={item.url}
        href={item.url}
        className={`focus:text-brand-primary focus:bg-action-focus focus:outline-action-focus rounded-[4.5px] font-[17px] text-white`}
        target="_blank"
        rel="noopener noreferrer"
      >
        <img src={item.src} alt={item.alt} />
      </a>
    ))}
  </nav>
);

const Separator = () => (
  <div
    className={cx(
      'my-4 border-b-2 border-[#ccc] opacity-10',
      'grid-in-separator',
    )}
  />
);

const BottomLinks = () => (
  <nav
    className={cx(
      'grid-in-bottom-links',
      'flex flex-col flex-wrap gap-x-[17px] gap-y-[8.5px] lg:flex-row lg:justify-center lg:font-[600]',
    )}
    aria-label="More council information"
  >
    {WCCFooterItems.bottomLinks.map((item) => (
      <a
        key={item.url}
        href={item.url}
        className={`hover:bg-action-button-hover focus:text-brand-primary focus:bg-action-focus focus:outline-action-focus text-body rounded-[4.5px] text-white`}
        rel="noopener noreferrer"
        target="_blank"
      >
        {item.text} <span className="sr-only">(link opens new window)</span>
      </a>
    ))}
  </nav>
);

const BottomSeparator = () => (
  <div
    className={cx(
      'mb-5 mt-4 border-b-2 border-[#ccc] opacity-10 lg:hidden',
      'grid-in-separator-two',
    )}
  />
);

export const WCCFooter: FunctionComponent = () => (
  <footer
    className={cx(`bg-brand-primary flex justify-center`, 'p-4 lg:p-2 lg:pb-8')}
  >
    <div className="grid-areas-footer-sm md:grid-areas-footer-lg md:grid-cols-footer-lg grid-cols-footer-sm max-auto container grid items-center justify-between md:w-full">
      <Feedback />

      <FooterMenu />

      <FooterLogo />

      <SocialMedia />

      <Separator />

      <BottomLinks />

      <BottomSeparator />
    </div>
  </footer>
);
